/**
 * Scout Studio — Diagnoza firmy (wersja podstawowa, reguły z profilu).
 * Zasada: brak → wyjaśnienie → akcja → narzędzie APIS/Scout.
 */
import { useEffect, useState } from 'react';
import { API } from '@/utils/api';
import {
  Stethoscope, Check, AlertTriangle, ArrowRight, ListPlus, Loader2, Wrench, Sparkles,
} from 'lucide-react';

const panel = 'rounded-2xl border border-[var(--sc-border)] bg-[var(--sc-surface)]';

export default function ScoutDiagnoza({ client, onNavigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [added, setAdded] = useState({});

  useEffect(() => {
    let live = true;
    setLoading(true); setData(null); setAdded({});
    fetch(`${API}/scout/diagnosis/${client.id}`)
      .then((r) => r.json())
      .then((d) => { if (live) { setData(d?.ok ? d : null); setLoading(false); } })
      .catch(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [client.id]);

  const addToPlan = async (gap) => {
    if (added[gap.id]) return;
    try {
      const res = await fetch(`${API}/scout/plan/add`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_id: client.id, items: [gap.action], source: 'diagnoza' }),
      });
      const d = await res.json();
      if (d?.ok) setAdded((s) => ({ ...s, [gap.id]: true }));
    } catch { /* noop */ }
  };

  return (
    <div className="space-y-6" data-testid="view-diagnoza">
      <div className="mb-2">
        <h2 className="text-lg font-bold flex items-center gap-2"><Stethoscope className="w-5 h-5 text-[#FF8A3D]" /> Diagnoza firmy</h2>
        <p className="text-xs text-[var(--sc-t45)] mt-0.5 ml-7">Co masz, czego brakuje i co warto zrobić najpierw — na podstawie Twojego profilu.</p>
      </div>

      {loading && (
        <div className={`${panel} p-8 text-center`} data-testid="diagnosis-loading">
          <Loader2 className="w-6 h-6 mx-auto animate-spin text-[#FF8A3D]" />
          <p className="text-sm text-[var(--sc-t45)] mt-3">Scout sprawdza profil firmy…</p>
        </div>
      )}

      {!loading && data && (
        <>
          {data.weak_profile && (
            <div className={`${panel} p-4 border-orange-400/30 flex flex-wrap items-center gap-3`} data-testid="diagnosis-weak-profile">
              <AlertTriangle className="w-4 h-4 text-[#FF8A3D]" />
              <p className="flex-1 text-sm text-[var(--sc-t70)]">Profil jest uzupełniony tylko w {data.completeness}%. Diagnoza będzie trafniejsza po jego uzupełnieniu.</p>
              <button onClick={() => onNavigate?.('firma')} data-testid="diagnosis-fill-profile-btn"
                className="rounded-lg bg-[#F26A21] hover:bg-[#FF7C33] px-3.5 py-2 text-xs font-bold text-white transition">Uzupełnij profil</button>
            </div>
          )}

          {/* Co zrobić najpierw */}
          {data.first_steps?.length > 0 && (
            <div className={`${panel} p-5`} data-testid="diagnosis-first-steps">
              <div className="text-sm font-bold mb-3 flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#FF8A3D]" /> Co zrobić najpierw</div>
              <div className="space-y-2.5">
                {data.first_steps.map((s, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-lg bg-orange-500/15 border border-orange-400/25 text-[#FF8A3D] text-xs font-extrabold flex items-center justify-center shrink-0">{i + 1}</span>
                    <p className="text-sm text-[var(--sc-t80)]">{s}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid lg:grid-cols-2 gap-5 items-start">
            {/* Co masz */}
            <div className={`${panel} p-5`} data-testid="diagnosis-have">
              <div className="text-sm font-bold mb-3 flex items-center gap-2 text-emerald-400"><Check className="w-4 h-4" /> Co już masz ({data.have?.length || 0})</div>
              {(data.have || []).length === 0 && <p className="text-sm text-[var(--sc-t40)]">Na razie niewiele — zacznij od uzupełnienia profilu.</p>}
              <div className="space-y-2.5">
                {(data.have || []).map((h, i) => (
                  <div key={i} className="flex items-start gap-2.5">
                    <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                    <div>
                      <div className="text-sm font-semibold">{h.label}</div>
                      {h.detail && <div className="text-xs text-[var(--sc-t45)] break-all">{h.detail}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Czego brakuje */}
            <div className="space-y-3" data-testid="diagnosis-gaps">
              <div className="text-sm font-bold flex items-center gap-2 text-[#FBBF24]"><AlertTriangle className="w-4 h-4" /> Czego brakuje ({data.gaps?.length || 0})</div>
              {(data.gaps || []).length === 0 && (
                <div className={`${panel} p-5 text-sm text-emerald-400`}>Świetnie — profil pokrywa wszystkie podstawy. Scout będzie pilnował dalszych kroków.</div>
              )}
              {(data.gaps || []).map((g) => (
                <div key={g.id} className={`${panel} p-4`} data-testid={`gap-${g.id}`}>
                  <div className="font-bold text-sm">{g.title}</div>
                  <p className="text-xs text-[var(--sc-t50)] mt-1 leading-relaxed">{g.explain}</p>
                  <div className="flex items-center gap-2 mt-2 text-xs text-[var(--sc-t70)]">
                    <ArrowRight className="w-3.5 h-3.5 text-[#FF8A3D] shrink-0" /> {g.action}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mt-3">
                    <button onClick={() => onNavigate?.(g.tool_view)} data-testid={`gap-tool-${g.id}`}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-orange-400/40 bg-orange-500/10 px-3 py-1.5 text-xs font-semibold text-orange-300 hover:bg-orange-500/20 transition">
                      <Wrench className="w-3.5 h-3.5" /> {g.tool}
                    </button>
                    <button onClick={() => addToPlan(g)} data-testid={`gap-plan-${g.id}`}
                      className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition ${added[g.id] ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-400/30' : 'border border-[var(--sc-border2)] text-[var(--sc-t50)] hover:bg-[var(--sc-hover)]'}`}>
                      {added[g.id] ? <Check className="w-3.5 h-3.5" /> : <ListPlus className="w-3.5 h-3.5" />} {added[g.id] ? 'W planie' : 'Dodaj do planu'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
